import { useEffect, useRef, useState, type JSX, type MouseEvent } from 'react';
import { useStore } from 'zustand';
import { FAVOURITE_NAME_MAX, FAVOURITE_NOTE_MAX, FAVOURITES_MAX } from '../../../core/model';
import type { LinkStatus } from '../../../net/api';
import { detectCaptionSupport } from '../../../engine/captions';
import * as channelApi from '../../../net/channel-api';
import { linkStatusOf } from '../../actions/outputs';
import { Icon, type IconName } from '../../components/Icon';
import { useProject, useRuntime, useUi, useUiContext } from '../../context';
import { CONTENT_LABEL_NAMES, STREAM_LANGUAGES } from '../../dialogs/settings/StreamInfoForm';
import { formatDuration, formatKbps } from '../../format';
import { summarizeHealth } from '../../health/healthModel';
import { useTicker } from '../../hooks/useTicker';
import { GuestAccess, recordButtonLabel, streamButtonLabel, useGuestApi, UNAVAILABLE, type GuestControlApi } from '../controls/ControlsPanel';
import { safely } from '../../safe';
import type { ActivityGroup, StreamManagerFigure, UiStore } from '../../uiStore';
import { ACTIVITY_GROUP_LABELS, ACTIVITY_GROUP_OF, ACTIVITY_SOUND_KINDS, viewersOf, type ActivityKind } from './activityModel';
import { playActivityBeep } from './activitySound';
import { activityStore, attachActivity } from './activityStore';
import { ObsModeCard } from './ObsModeCard';
import './streamManager.css';

const STREAM_INFO_POLL_MS = 15_000;
const CHANNEL_POLL_MS = 60_000;
const BRAND_SAFETY_POLL_MS = 60_000;
/** After an unavailable answer or a failure: the channel is not rated now, ask again much later. */
const BRAND_SAFETY_BACKOFF_MS = 5 * 60_000;

const STATUS_LABELS: Record<LinkStatus, string> = {
  idle: 'Offline',
  connecting: 'Connecting…',
  live: 'Live',
  reconnecting: 'Reconnecting…',
  error: 'Error',
};

const KIND_ICONS: Record<ActivityKind, IconName> = {
  live: 'broadcast',
  offline: 'broadcast',
  reconnecting: 'refresh',
  error: 'warning',
  viewers: 'eye',
  'guest-joined': 'users',
  'guest-left': 'users',
  'recording-started': 'record',
  'recording-stopped': 'record',
  clip: 'film',
  health: 'warning',
};

/** `HH:MM:SS` in local time, 24-hour. */
export function clockOf(at: number): string {
  const d = new Date(at);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

type CaptionPill = 'listening' | 'standby' | 'off' | 'unsupported' | 'error';
const CAPTION_PILL_LABELS: Record<CaptionPill, string> = {
  listening: 'CC listening',
  standby: 'CC on',
  off: 'CC off',
  unsupported: 'CC unsupported',
  error: 'CC error',
};
const CAPTION_PILL_TITLES: Record<CaptionPill, string> = {
  listening: 'Automatic captions are being generated and sent to viewers',
  standby: 'Automatic captions start when you go live on ompeer.com',
  off: 'Automatic captions are off',
  unsupported: 'This browser cannot generate captions',
  error: 'Automatic captions are failing',
};

const FIGURE_LABELS: Record<StreamManagerFigure, string> = {
  session: 'Session',
  viewers: 'Viewers',
  followers: 'Followers',
  bitrate: 'Bitrate',
  health: 'Health',
  brandSafety: 'Brand safety',
};

/** The adserver's safety categories (adapters/ohmypeer mapping), as the creator reads them. */
const SAFETY_CATEGORY_NAMES: Record<string, string> = {
  alcohol: 'alcohol',
  drugs: 'drogas',
  gambling: 'apuestas',
  politics: 'política',
  profanity: 'malas palabras',
  sexual: 'contenido sexual',
  tragedy: 'tragedias',
  violence: 'violencia',
};
const safetyCategoryName = (category: string): string => SAFETY_CATEGORY_NAMES[category] ?? category.replace(/_/g, ' ');

/** `hace 12 s` / `hace 3 min` since the evaluation. */
function sinceOf(evaluatedAt: string, now: number): string {
  const seconds = Math.max(0, Math.round((now - Date.parse(evaluatedAt)) / 1000));
  if (!Number.isFinite(seconds)) return '';
  return seconds < 60 ? `hace ${seconds} s` : `hace ${Math.floor(seconds / 60)} min`;
}

type FigureTone = 'ok' | 'warn' | 'danger' | 'neutral';

/** The BRAND SAFETY cell of an available rating: its value, colour and tooltip. */
export function brandSafetyFigure(safety: Extract<channelApi.BrandSafety, { available: true }>, now: number): { value: string; tone: FigureTone; title: string } {
  const since = sinceOf(safety.evaluated_at, now);
  const title = (reason: string): string => [reason, since && `Evaluado ${since}`].filter(Boolean).join(' · ');
  switch (safety.rating) {
    case 'safe':
      return { value: safety.doubts ? 'Seguro · con dudas' : 'Seguro', tone: 'ok', title: title(safety.reason) };
    case 'unsafe': {
      const names = safety.categories.map(safetyCategoryName);
      return {
        value: names.length > 0 ? `No apto · ${names[0]}` : 'No apto',
        tone: 'danger',
        title: title(names.length > 1 ? `${safety.reason} (${names.join(', ')})` : safety.reason),
      };
    }
    default:
      return { value: 'Analizando', tone: 'neutral', title: title(`Necesita ${safety.window_minutes} min de análisis. ${safety.reason}`) };
  }
}

type RatedSafety = Extract<channelApi.BrandSafety, { available: true }>;

const WEEKDAYS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

/** '' today, 'mañana ' tomorrow, else 'lun 27 ' (local time). */
function dayOf(at: number, now: number): string {
  const day = (t: number): number => {
    const d = new Date(t);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  };
  const days = Math.round((day(at) - day(now)) / 86_400_000);
  if (days <= 0) return '';
  if (days === 1) return 'mañana ';
  const d = new Date(at);
  return `el ${WEEKDAYS[d.getDay()]} ${d.getDate()} `;
}

/** The "Monetización bloqueada" explanation of a blocked rating: why, until when, and that the stream is unaffected. */
export function blockExplanation(safety: RatedSafety, now: number, live = true): { title: string; reason: string; unblock: string; footer: string } {
  const names = safety.categories.map(safetyCategoryName);
  const what = `Se detectó contenido no apto para marcas${names.length > 0 ? ` (${names.join(', ')})` : ''}`;
  const reason =
    safety.block_reason === 'recurrent_channel'
      ? `${what} en varias transmisiones recientes. Mientras dure, no se muestran anuncios en tu canal.`
      : live
        ? `${what} en esta transmisión. Los anuncios se pausan hasta que pasen 10 minutos sin ese contenido.`
        : `${what} en tu última transmisión. Los anuncios se pausan hasta que pasen 10 minutos sin ese contenido.`;
  const at = safety.recovers_at ? Date.parse(safety.recovers_at) : NaN;
  let unblock = 'Se desbloquea cuando deje de detectarse ese contenido.';
  if (Number.isFinite(at) && at > now) {
    const minutes = Math.ceil((at - now) / 60_000);
    unblock = `Se desbloquea aprox. ${dayOf(at, now)}a las ${clockOf(at).slice(0, 5)}${at - now < 2 * 3_600_000 ? ` (en ${minutes} min)` : ''}`;
  }
  return { title: 'Monetización bloqueada', reason, unblock, footer: 'Esto no afecta tu transmisión, solo la publicidad.' };
}

/** The BRAND SAFETY cell of a blocked rating: a button that opens its explanation (Escape or a press outside closes it). */
function BlockedFigure({ safety, title, live }: { safety: RatedSafety; title: string; live: boolean }): JSX.Element {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    popover.current?.focus();
    const onPointerDown = (event: PointerEvent): void => {
      if (event.target instanceof Node && (popover.current?.contains(event.target) || button.current?.contains(event.target))) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);
  const close = (): void => {
    setOpen(false);
    button.current?.focus();
  };
  const text = open ? blockExplanation(safety, Date.now(), live) : null;
  return (
    <div className="sm-safety">
      <button
        ref={button}
        type="button"
        className="sm-figure sm-figure-button"
        title={title}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <div className="sm-figure-label">Brand safety</div>
        <div className="sm-figure-value danger" aria-label="Brand safety">
          Bloqueado
        </div>
      </button>
      {text ? (
        <div
          ref={popover}
          className="sm-safety-popover"
          role="dialog"
          aria-label={text.title}
          tabIndex={-1}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            close();
          }}
        >
          <div className="sm-safety-popover-title">{text.title}</div>
          <p>{text.reason}</p>
          <p>{text.unblock}</p>
          <p className="ui-dim">{text.footer}</p>
        </div>
      ) : null}
    </div>
  );
}

/** Opens a menu of checkable entries under the button that was pressed. */
function openCheckMenu<K extends string>(
  ui: UiStore,
  event: MouseEvent<HTMLButtonElement>,
  labels: Record<K, string>,
  state: Record<K, boolean>,
  onToggle: (key: K) => void,
): void {
  const rect = event.currentTarget.getBoundingClientRect();
  ui.getState().openMenu({
    x: rect.left,
    y: rect.bottom,
    returnFocus: event.currentTarget,
    items: (Object.keys(labels) as K[]).map((key) => ({ label: labels[key], checked: state[key], onSelect: () => onToggle(key) })),
  });
}

interface RemoteInfo {
  /** The channel's current title on the control plane. */
  title: string;
  slug: string | null;
  channelName: string | null;
  viewerCount: number;
}

function Figure({ label, value, tone, title }: { label: string; value: string; tone?: FigureTone; title?: string }): JSX.Element {
  return (
    <div className="sm-figure" title={title}>
      <div className="sm-figure-label">{label}</div>
      <div className={tone ? `sm-figure-value ${tone}` : 'sm-figure-value'} aria-label={label}>
        {value}
      </div>
    </div>
  );
}

function Tile({ icon, label, onClick, disabled, title, pressed }: { icon: IconName; label: string; onClick(): void; disabled?: boolean; title?: string; pressed?: boolean }): JSX.Element {
  return (
    <button type="button" className="sm-tile" onClick={onClick} disabled={disabled} title={title} aria-pressed={pressed}>
      <Icon name={icon} size={18} />
      <span>{label}</span>
    </button>
  );
}

/** `channelTitle` is the title the channel has on ompeer.com: it stays unless one is typed here. */
function StreamInfoCard({ channelTitle, onEdit }: { channelTitle: string; onEdit(): void }): JSX.Element {
  const info = useProject((p) => p.channel.info);
  const language = STREAM_LANGUAGES.find(([code]) => code === info.language)?.[1] ?? (info.language || 'Not set');
  return (
    <section className="sm-card sm-info" aria-label="Stream info">
      <div className="sm-card-head">
        <h3 className="sm-card-title">Stream info</h3>
        <button type="button" className="ui-btn" onClick={onEdit}>
          <Icon name="sliders" /> Edit stream info
        </button>
      </div>
      <div className="sm-info-title">
        {info.title || channelTitle || <span className="ui-dim">No title</span>}
      </div>
      <div className="sm-info-meta">
        <span>{info.category || <span className="ui-dim">No category</span>}</span>
        <span className="ui-dim">·</span>
        <span>{language}</span>
        {info.rerun ? <span className="ui-badge sm-rerun">Rerun</span> : null}
      </div>
      {info.tags.length > 0 ? (
        <ul className="sm-tags" aria-label="Stream tags">
          {info.tags.map((tag) => (
            <li key={tag} className="sm-tag">
              {tag}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="ui-dim" title={info.contentLabels.map((l) => CONTENT_LABEL_NAMES[l]).join(', ') || undefined}>
        {info.contentLabels.length === 0
          ? 'No content classification'
          : `${info.contentLabels.length} content label${info.contentLabels.length === 1 ? '' : 's'}`}
      </div>
    </section>
  );
}

function ActivityFeed(): JSX.Element {
  const { ui } = useUiContext();
  const all = useStore(activityStore, (s) => s.events);
  const filter = useUi((s) => s.prefs.streamManagerActivityFilter);
  const sound = useUi((s) => s.prefs.streamManagerActivitySound);
  const events = all.filter((event) => filter[ACTIVITY_GROUP_OF[event.kind]]);
  const filtered = Object.values(filter).some((on) => !on);

  useEffect(() => {
    if (!sound) return;
    let newest = activityStore.getState().events[0]?.id ?? 0;
    return activityStore.subscribe((state) => {
      const fresh = state.events.filter((event) => event.id > newest);
      if (fresh.length === 0) return;
      newest = fresh[0]!.id;
      if (fresh.some((event) => ACTIVITY_SOUND_KINDS.has(event.kind))) playActivityBeep();
    });
  }, [sound]);

  const toggleGroup = (group: ActivityGroup): void => {
    const prefs = ui.getState().prefs;
    ui.getState().setPrefs({ streamManagerActivityFilter: { ...prefs.streamManagerActivityFilter, [group]: !prefs.streamManagerActivityFilter[group] } });
  };

  return (
    <section className="sm-card sm-activity" aria-label="Activity feed">
      <div className="sm-card-head">
        <h3 className="sm-card-title">Activity</h3>
        <div className="ui-row">
          <button
            type="button"
            className="ui-btn"
            aria-haspopup="menu"
            aria-label="Filter activity"
            onClick={(e) => openCheckMenu(ui, e, ACTIVITY_GROUP_LABELS, ui.getState().prefs.streamManagerActivityFilter, toggleGroup)}
          >
            <Icon name="sliders" /> {filtered ? 'Filter (on)' : 'Filter'}
          </button>
          <button
            type="button"
            className="ui-btn"
            aria-pressed={sound}
            title="Beep when a viewer or guest event arrives"
            onClick={() => ui.getState().setPrefs({ streamManagerActivitySound: !sound })}
          >
            <Icon name={sound ? 'speaker' : 'speakerOff'} /> Sound
          </button>
          <button type="button" className="ui-btn" disabled={all.length === 0} onClick={() => activityStore.getState().clear()}>
            Clear
          </button>
        </div>
      </div>
      <ol className="sm-feed" role="log" aria-label="Activity">
        {events.length === 0 ? (
          <li className="ui-dim sm-feed-empty">
            {all.length > 0 ? 'Every event so far is hidden by the filter.' : 'Quiet so far. Viewers, guests, recording and stream events appear here.'}
          </li>
        ) : (
          events.map((event) => (
            <li key={event.id} className={`sm-feed-row ${event.kind}`}>
              <time className="sm-feed-time" dateTime={new Date(event.at).toISOString()}>
                {clockOf(event.at)}
              </time>
              <Icon name={KIND_ICONS[event.kind]} size={14} />
              <span className="ui-grow">{event.text}</span>
            </li>
          ))
        )}
      </ol>
    </section>
  );
}

function Chat({ room, portalUrl, swarm }: { room: string | null; portalUrl: string; swarm: boolean }): JSX.Element {
  const [hidden, setHidden] = useState(false);
  const url = room ? channelApi.popoutChatUrl(portalUrl, room) : null;
  const sameOrigin = url !== null && new URL(url).origin === location.origin;
  return (
    <section className="sm-card sm-chat" aria-label="Chat">
      <div className="sm-card-head">
        <h3 className="sm-card-title">Chat</h3>
        {url ? (
          <div className="ui-row">
            <a className="ui-btn" href={url} target="_blank" rel="noopener noreferrer">
              <Icon name="link" /> Open in new window
            </a>
            <a className="ui-btn" href={`${portalUrl.replace(/\/+$/, '')}/settings`} target="_blank" rel="noopener noreferrer">
              <Icon name="gear" /> Open chat settings
            </a>
            <button type="button" className="ui-btn" aria-pressed={hidden} onClick={() => setHidden(!hidden)}>
              <Icon name={hidden ? 'eye' : 'eyeOff'} /> {hidden ? 'Show' : 'Hide'}
            </button>
          </div>
        ) : null}
      </div>
      {!swarm ? (
        <p className="ui-dim">Chat is available when streaming to ompeer.com (Settings → Stream).</p>
      ) : !url ? (
        <p className="ui-dim">The chat appears once ompeer.com knows your channel: check the stream key in Settings → Stream, or go live.</p>
      ) : sameOrigin ? (
        <p className="ui-dim">The portal URL must not be this studio's origin.</p>
      ) : hidden ? (
        <p className="ui-dim">Chat hidden.</p>
      ) : (
        <iframe className="sm-chat-frame" title="Chat" src={url} sandbox="allow-scripts allow-same-origin allow-forms allow-popups" referrerPolicy="no-referrer" />
      )}
    </section>
  );
}

/**
 * People the streamer often streams with ("Favourites"). Invite creates a personal invitation in
 * their name through the same API as "Invite a guest" and copies the link.
 */
function Favourites({ api, canInvite }: { api: GuestControlApi; canInvite: boolean }): JSX.Element {
  const { studio } = useUiContext();
  const favourites = useProject((p) => p.channel.favourites);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const taken = favourites.some((f) => f.name.toLowerCase() === name.trim().toLowerCase());
  const full = favourites.length >= FAVOURITES_MAX;

  const add = (e: { preventDefault(): void }): void => {
    e.preventDefault();
    if (!name.trim() || taken || full) return;
    void safely(studio, () => {
      studio.commands.setChannelFavourites([...favourites, { name, note }]);
      setName('');
      setNote('');
    });
  };
  const remove = (target: string): void =>
    void safely(studio, () => studio.commands.setChannelFavourites(favourites.filter((f) => f.name !== target)));
  const invite = (target: string): void =>
    void safely(studio, async () => {
      if (!api.createInvite) return;
      const created = await api.createInvite({ name: target });
      try {
        await navigator.clipboard.writeText(created.url);
        studio.notify('info', 'Guest invitation created and copied.');
      } catch {
        studio.notify('info', 'Guest invitation created.');
      }
    }, 'Could not create the invitation');

  const inviteTitle = !api.createInvite ? UNAVAILABLE : canInvite ? 'Create a personal invitation and copy the link' : 'Turn on "Accept guests" to invite';
  return (
    <div className="ui-col" style={{ gap: 4 }} aria-label="Favourites">
      <div className="ui-dim">Favourites</div>
      {favourites.length === 0 ? (
        <p className="ui-dim">No favourites yet. Add the people you stream with to invite them in one click.</p>
      ) : (
        <div className="ui-col" style={{ gap: 4 }} role="list" aria-label="Favourite people">
          {favourites.map((f) => (
            <div key={f.name} className="peer-row" role="listitem">
              <Icon name="users" size={14} />
              <span className="ui-grow ui-ellipsis" title={f.note || undefined}>
                {f.name}
                {f.note ? <span className="ui-dim"> — {f.note}</span> : null}
              </span>
              <button
                type="button"
                className="ui-btn"
                aria-label={`Invite ${f.name}`}
                title={inviteTitle}
                disabled={!api.createInvite || !canInvite}
                onClick={() => invite(f.name)}
              >
                <Icon name="link" size={14} /> Invite
              </button>
              <button type="button" className="ui-icon-btn" aria-label={`Remove ${f.name} from favourites`} title="Remove from favourites" onClick={() => remove(f.name)}>
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      <form className="url-row" aria-label="Add favourite" onSubmit={add}>
        <input className="ui-input" placeholder="Name" aria-label="Favourite's name" value={name} maxLength={FAVOURITE_NAME_MAX} onChange={(e) => setName(e.target.value)} />
        <input className="ui-input" placeholder="Note (optional)" aria-label="Favourite's note" value={note} maxLength={FAVOURITE_NOTE_MAX} onChange={(e) => setNote(e.target.value)} />
        <button type="submit" className="ui-btn" disabled={!name.trim() || taken || full} title={full ? `At most ${FAVOURITES_MAX} favourites` : taken ? 'Already a favourite' : undefined}>
          <Icon name="plus" size={14} /> Add favourite
        </button>
      </form>
    </div>
  );
}

/**
 * Guest controls ("Collaboration", Stream Together). The same signalling room serves guests in
 * peer-to-peer and swarm mode, so both show the same controls; WHIP has no room for guests.
 */
function Collaboration({ guests }: { guests: boolean }): JSX.Element {
  const api = useGuestApi();
  const { studio } = useUiContext();
  const connected = useRuntime((r) => r.broadcast.peers).filter((peer) => peer.role === 'guest');
  const allowGuests = useRuntime((r) => r.broadcast.allowGuests);
  return (
    <section id="sm-collab" tabIndex={-1} className="sm-card sm-collab" aria-label="Collaboration">
      <div className="sm-card-head">
        <h3 className="sm-card-title">Collaboration</h3>
      </div>
      {guests ? (
        <>
          <p className="ui-dim">Invite guests to send their camera and microphone into the show.</p>
          <GuestAccess api={api} />
          {connected.length > 0 ? (
            <div className="ui-col" style={{ gap: 4 }} role="list" aria-label="Guests in the show">
              {connected.map((peer) => (
                <div key={peer.peerId} className="peer-row" role="listitem">
                  <Icon name="users" size={14} />
                  <span className="ui-grow ui-ellipsis">{peer.name || 'Guest'}</span>
                  <span className="ui-dim">{peer.connectionState}</span>
                  <button
                    type="button"
                    className="ui-icon-btn"
                    aria-label={`Remove ${peer.name || 'guest'}`}
                    title="Remove: this guest cannot come back in this broadcast"
                    onClick={() => void safely(studio, () => studio.kickPeer(peer.peerId))}
                  >
                    <Icon name="close" size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <p className="ui-dim">Guests join in peer-to-peer or swarm mode (Settings → Stream).</p>
      )}
      <Favourites api={api} canInvite={guests && allowGuests} />
    </section>
  );
}

/** The connected channel (avatar, name, page link), or how to connect when there is no stream key. */
function ChannelHeader({ streamKey, portalUrl, slug, avatarUrl, name }: { streamKey: string; portalUrl: string; slug: string | null; avatarUrl: string | null; name: string | null }): JSX.Element | null {
  const portal = portalUrl.replace(/\/+$/, '');
  if (streamKey === '') {
    return (
      <section className="sm-channel" aria-label="Channel">
        <span className="ui-dim">
          Not connected — open{' '}
          <a href={`${portal}/settings/stream`} target="_blank" rel="noopener noreferrer">
            Settings → Stream
          </a>{' '}
          on the portal
        </span>
      </section>
    );
  }
  if (!slug) return null;
  return (
    <section className="sm-channel" aria-label="Channel">
      {avatarUrl ? <img className="sm-channel-avatar" src={avatarUrl} alt="" /> : null}
      <span className="sm-channel-title">{name}</span>
      <a href={channelApi.channelPageUrl(portalUrl, slug)} target="_blank" rel="noopener noreferrer">
        /channel/{slug}
      </a>
    </section>
  );
}

/** A creator dashboard: status, stream info, shortcuts, the activity feed and the chat. */
export function StreamManagerPanel(): JSX.Element {
  const { studio, ui, actions } = useUiContext();
  const mode = useProject((p) => p.output.streaming.mode);
  const streamKey = useProject((p) => p.output.streaming.swarmStreamKey.trim());
  const apiUrl = useProject((p) => p.output.streaming.swarmApiUrl);
  const portalUrl = useProject((p) => p.channel.portalUrl);
  const linkStatus = useRuntime((r) => linkStatusOf(mode, r));
  const viewers = useRuntime((r) => viewersOf(mode, r));
  const bitrate = useRuntime((r) => {
    if (mode === 'swarm') return r.swarm.stats?.encodedKbps ?? null;
    const peers = Object.values(r.network);
    return peers.length === 0 ? null : peers.reduce((sum, peer) => sum + peer.outKbps, 0);
  });
  const health = useRuntime((r) => summarizeHealth(r.health).label);
  const streamId = useRuntime((r) => r.swarm.streamId);
  const swarmLive = useRuntime((r) => mode === 'swarm' && r.swarm.status === 'live');
  const recording = useRuntime((r) => r.recording.status);
  const replayRunning = useRuntime((r) => r.replay.status === 'running');
  const clips = useProject((p) => p.channel.clips);
  const clipsEnabled = clips.enabled;
  const captions = useProject((p) => p.output.streaming.captions);
  const captionState = useRuntime((r) => r.captions);
  const captionSupport = detectCaptionSupport(window);
  const captionPill: CaptionPill =
    captionSupport.reason !== null || captionState.status === 'unsupported'
      ? 'unsupported'
      : !captions.enabled
        ? 'off'
        : captionState.status === 'listening'
          ? 'listening'
          : captionState.status === 'error'
            ? 'error'
            : 'standby';
  const busy = useUi((s) => s.busy);
  const figures = useUi((s) => s.prefs.streamManagerFigures);
  // Inside the portal's Stream Manager the header above the frame already shows the session state and these
  // figures, so the strip keeps only what the portal has no view of (captions, channel, reachability).
  const embedded = useUi((s) => s.embedded);
  const liveSince = useStore(activityStore, (s) => s.liveSince);
  const knownSlug = useStore(activityStore, (s) => s.channelSlug);
  const now = useTicker(liveSince !== null, 1000);

  const [remote, setRemote] = useState<RemoteInfo | null>(null);
  const [channel, setChannel] = useState<channelApi.RemoteChannel | null>(null);
  const [unreachable, setUnreachable] = useState(false);

  useEffect(() => attachActivity(studio), [studio]);

  const polling = mode === 'swarm' && streamKey !== '';
  useEffect(() => {
    if (!polling) {
      setRemote(null);
      setUnreachable(false);
      activityStore.getState().setChannelSlug(null);
      return;
    }
    let cancelled = false;
    const poll = async (): Promise<void> => {
      try {
        const stream = await channelApi.fetchStreamInfo({ apiUrl, streamKey });
        if (cancelled) return;
        setUnreachable(false);
        setRemote(stream ? { title: stream.title, slug: stream.channelSlug, channelName: stream.channelName, viewerCount: stream.viewerCount } : null);
        activityStore.getState().setChannelSlug(stream?.channelSlug ?? null);
      } catch {
        if (!cancelled) setUnreachable(true);
      }
    };
    void poll();
    const handle = setInterval(() => void poll(), STREAM_INFO_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [polling, apiUrl, streamKey]);

  const slug = polling ? (remote?.slug ?? knownSlug) : null;
  useEffect(() => {
    setChannel(null);
    if (!slug) return;
    let cancelled = false;
    const poll = async (): Promise<void> => {
      try {
        const channel = await channelApi.fetchChannel({ apiUrl, slug });
        if (cancelled) return;
        setUnreachable(false);
        setChannel(channel);
      } catch {
        if (!cancelled) setUnreachable(true);
      }
    };
    void poll();
    const handle = setInterval(() => void poll(), CHANNEL_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [apiUrl, slug]);

  const followers = channel?.followerCount ?? null;
  const live = linkStatus === 'live' || linkStatus === 'reconnecting';

  // BRAND SAFETY: on the swarm with the figure shown; hidden when unavailable or failing. Offline it is polled
  // every 60 s and shown only for a channel-level block (monetization blocked before go-live).
  const [safety, setSafety] = useState<channelApi.BrandSafety | null>(null);
  // The portal header has no safety rating, so this figure also belongs in the embed.
  const safetyPolling = polling && figures.brandSafety;
  useEffect(() => {
    setSafety(null);
    if (!safetyPolling) return;
    let cancelled = false;
    let handle: ReturnType<typeof setTimeout> | undefined;
    const poll = async (): Promise<void> => {
      let next = BRAND_SAFETY_BACKOFF_MS;
      try {
        const answer = await channelApi.fetchBrandSafety({ apiUrl, streamKey });
        if (cancelled) return;
        setSafety(answer);
        if (answer.available || !live) next = BRAND_SAFETY_POLL_MS;
      } catch {
        if (cancelled) return;
        setSafety(null);
      }
      handle = setTimeout(() => void poll(), next);
    };
    void poll();
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [safetyPolling, live, apiUrl, streamKey]);
  const shownSafety = safetyPolling && safety?.available && (live || safety.blocked === true) ? safety : null;
  const safetyFigure = shownSafety ? brandSafetyFigure(shownSafety, Date.now()) : null;
  const shownViewers = viewers === null ? null : !live && remote ? remote.viewerCount : viewers;
  const pageUrl = slug ? channelApi.channelPageUrl(portalUrl, slug) : null;
  const openEdit = (): void => ui.getState().openDialog({ type: 'edit-stream-info' });
  const copyLink = (): void =>
    void safely(studio, async () => {
      if (!pageUrl) return;
      await navigator.clipboard.writeText(pageUrl);
      studio.notify('info', 'Channel link copied.');
    }, 'Could not copy');
  const startRaid = (): void =>
    ui.getState().openDialog({
      type: 'prompt',
      title: 'Raid a channel',
      label: 'Channel name',
      initial: '',
      confirmLabel: 'Raid',
      onSubmit: (target) =>
        void safely(studio, async () => {
          const r = await studio.raidChannel(target);
          studio.notify('info', `Raided ${r.target} with ${r.viewers} viewers.`);
          activityStore.getState().push([{ at: Date.now(), kind: 'offline', text: `Raided ${target}` }]);
        }, 'Could not raid'),
    });

  const makeClip = (): void =>
    void safely(studio, async () => {
      const r = await studio.saveClip();
      studio.notify('info', `Clip saved: ${r.fileName}`);
      activityStore.getState().push([{ at: Date.now(), kind: 'clip', text: 'Clip saved' }]);
    }, 'Could not save the clip');

  return (
    <div className="ui-panel">
      <div className="ui-panel-body sm-root">
        <ChannelHeader
          streamKey={streamKey}
          portalUrl={portalUrl}
          slug={slug}
          avatarUrl={channel?.avatarUrl ?? null}
          name={channel?.displayName || channel?.name || remote?.channelName || slug}
        />
        <section className="sm-strip" aria-label="Stream status">
          {embedded ? null : (
            <div className={`sm-pill ${linkStatus}`} aria-label="Stream state">
              <span className="sm-pill-dot" aria-hidden="true" />
              {STATUS_LABELS[linkStatus]}
            </div>
          )}
          <div
            className={`sm-pill sm-cc ${captionPill}`}
            aria-label="Captions state"
            title={captionPill === 'unsupported' ? (captionSupport.reason ?? captionState.error ?? undefined) : captionPill === 'error' ? (captionState.error ?? undefined) : CAPTION_PILL_TITLES[captionPill]}
          >
            {CAPTION_PILL_LABELS[captionPill]}
          </div>
          {!embedded && figures.session ? <Figure label="Session" value={liveSince === null ? '—' : formatDuration(Math.max(0, now - liveSince))} /> : null}
          {!embedded && figures.viewers ? <Figure label="Viewers" value={shownViewers === null ? '—' : String(shownViewers)} /> : null}
          {!embedded && figures.followers ? <Figure label="Followers" value={followers === null ? '—' : String(followers)} /> : null}
          {!embedded && figures.bitrate ? <Figure label="Bitrate" value={formatKbps(bitrate)} /> : null}
          {!embedded && figures.health ? <Figure label="Health" value={health} /> : null}
          {shownSafety && safetyFigure ? (
            shownSafety.blocked === true ? (
              <BlockedFigure safety={shownSafety} title={safetyFigure.title} live={live} />
            ) : (
              <Figure label="Brand safety" value={safetyFigure.value} tone={safetyFigure.tone} title={safetyFigure.title} />
            )
          ) : null}
          {remote?.channelName ? <span className="ui-dim sm-channel-name">{remote.channelName}</span> : null}
          {unreachable ? <span className="ui-dim sm-unreachable">ompeer.com unreachable</span> : null}
          {embedded ? null : (
          <button
            type="button"
            className="ui-icon-btn sm-strip-menu"
            aria-haspopup="menu"
            aria-label="Choose figures"
            title="Choose figures"
            onClick={(e) =>
              openCheckMenu(ui, e, FIGURE_LABELS, ui.getState().prefs.streamManagerFigures, (figure) => {
                const current = ui.getState().prefs.streamManagerFigures;
                ui.getState().setPrefs({ streamManagerFigures: { ...current, [figure]: !current[figure] } });
              })
            }
          >
            <Icon name="dots" />
          </button>
          )}
        </section>

        <StreamInfoCard channelTitle={remote?.title ?? ''} onEdit={openEdit} />
        {captionPill === 'listening' && captionState.lastText ? (
          <div className="ui-dim ui-ellipsis sm-caption-line" aria-label="Last caption" title={captionState.lastText}>
            {captionState.lastText}
          </div>
        ) : null}

        <section className="sm-card sm-actions" aria-label="Quick actions">
          <div className="sm-card-head">
            <h3 className="sm-card-title">Quick actions</h3>
          </div>
          <div className="sm-tiles">
            <Tile icon="sliders" label="Edit stream info" onClick={openEdit} />
            <Tile icon="broadcast" label={streamButtonLabel(linkStatus, busy.streaming)} disabled={busy.streaming} onClick={() => actions.toggleStreaming()} />
            <Tile
              icon="record"
              label={recordButtonLabel(recording, busy.recording)}
              disabled={busy.recording || recording === 'stopping' || recording === 'starting'}
              onClick={() => actions.toggleRecording()}
            />
            <Tile icon="snapshot" label="Screenshot" onClick={() => actions.screenshot()} />
            <Tile
              icon="text"
              label="Captions"
              pressed={captions.enabled}
              disabled={captionSupport.reason !== null}
              title={captionSupport.reason ?? (captions.enabled ? 'Turn automatic captions off' : 'Turn automatic captions on')}
              onClick={() => void safely(studio, () => studio.commands.setStreamingSettings({ captions: { ...captions, enabled: !captions.enabled } }))}
            />
            <Tile
              icon="film"
              label="Make a clip"
              disabled={!clipsEnabled || !replayRunning}
              title={!clipsEnabled ? 'Enable clips in Settings → Channel' : replayRunning ? 'Save the last moments of the program as a recording' : 'Available while you stream or record'}
              onClick={makeClip}
            />
            <Tile
              icon="globe"
              label="Open channel page"
              disabled={!pageUrl}
              title={pageUrl ? undefined : 'Known once ompeer.com reports your channel'}
              onClick={() => {
                if (pageUrl) window.open(pageUrl, '_blank', 'noopener');
              }}
            />
            <Tile icon="copy" label="Copy channel link" disabled={!pageUrl} onClick={copyLink} />
            <Tile icon="gear" label="Channel settings" onClick={() => ui.getState().openDialog({ type: 'settings', tab: 'channel' })} />
            <Tile
              icon="users"
              label="Start a raid"
              disabled={!swarmLive}
              title={swarmLive ? 'Send your viewers to another channel and end the stream' : 'Available while live on ompeer.com'}
              onClick={startRaid}
            />
            <Tile icon="check" label="Manage goals" onClick={() => ui.getState().openDialog({ type: 'settings', tab: 'channel' })} />
            <Tile icon="puzzle" label="Streaming tools" onClick={() => ui.getState().openDialog({ type: 'streaming-tools' })} />
          </div>
          <div className="ui-row sm-clip-status">
            <span className="ui-dim">
              {clips.enabled ? `Viewer clips: on · ${clips.whoCanClip === 'following' ? 'followers' : 'anyone'} · ${clips.seconds} s` : 'Viewer clips: off'}
            </span>
            <button type="button" className="ui-btn" title="Viewers clip from the segments their player already holds" onClick={() => ui.getState().openDialog({ type: 'settings', tab: 'channel' })}>
              Clip settings
            </button>
          </div>
        </section>

        <Collaboration guests={mode !== 'whip'} />

        <ObsModeCard />

        <ActivityFeed />
        <Chat room={slug ?? (mode === 'swarm' ? streamId : null)} portalUrl={portalUrl} swarm={mode === 'swarm'} />
      </div>
    </div>
  );
}
